import React, { useState } from "react";
import {
  Badge,
  Button,
  Card,
  Combobox,
  DataTable,
  Drawer,
  EmptyState,
  Field,
  Input,
  Modal,
  PasswordInput,
  Select,
  Skeleton,
  SkeletonText,
  Tabs,
  useToast,
  PlusIcon,
  SearchIcon
} from "../../ui";

/**
 * Component-kit gallery — a living reference for the redesign (Phase 0
 * deliverable, review aid for the Coordinator/Evan). ALL data on this page is
 * invented placeholder data per the runbook's PII rule; nothing is fetched.
 * Remove or gate this route once the redesign phases are complete.
 */

const SAMPLE_ROWS = [
  {
    id: "1",
    household: "Jordan & Casey Sample",
    policies: 3,
    premium: 4129,
    renewal: "2026-09-10",
    status: "Active"
  },
  {
    id: "2",
    household: "Riley Example",
    policies: 1,
    premium: 1240,
    renewal: "2026-07-28",
    status: "Active"
  },
  {
    id: "3",
    household: "Kai Placeholder",
    policies: 2,
    premium: 2864,
    renewal: "2026-08-02",
    status: "Pending"
  },
  {
    id: "4",
    household: "Morgan Demo",
    policies: 4,
    premium: 6470,
    renewal: "2027-01-15",
    status: "Active"
  },
  {
    id: "5",
    household: "Avery Fictional",
    policies: 1,
    premium: 980,
    renewal: "2026-07-20",
    status: "Lapsed"
  },
  {
    id: "6",
    household: "Quinn Specimen",
    policies: 2,
    premium: 3115,
    renewal: "2026-11-05",
    status: "Active"
  }
];

const money = (n) =>
  n.toLocaleString("en-US", { style: "currency", currency: "USD", maximumFractionDigits: 0 });
const dateFmt = (iso) =>
  new Date(`${iso}T00:00:00`).toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric"
  });

const STATUS_VARIANT = { Active: "success", Pending: "info", Lapsed: "danger" };

function KitPage() {
  const toast = useToast();
  const [modalOpen, setModalOpen] = useState(false);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [tab, setTab] = useState("overview");
  const [agent, setAgent] = useState(null);

  return (
    <div className="shell-page" style={{ display: "grid", gap: "var(--space-5)" }}>
      <div className="shell-page__titlerow" style={{ marginBottom: 0 }}>
        <h1 className="shell-page__title">Design kit</h1>
        <Badge variant="neutral">Sample data only</Badge>
      </div>
      <Card title="Buttons">
        <div
          style={{ display: "flex", gap: "var(--space-3)", flexWrap: "wrap", alignItems: "center" }}
        >
          <Button variant="primary" icon={<PlusIcon size={14} />}>
            New quote
          </Button>
          <Button variant="secondary">Secondary</Button>
          <Button variant="ghost">Ghost</Button>
          <Button variant="destructive">Delete</Button>
          <Button variant="primary" loading>
            Saving
          </Button>
          <Button variant="secondary" size="sm">
            Small
          </Button>
        </div>
      </Card>

      <Card title="Badges">
        <div style={{ display: "flex", gap: "var(--space-3)", flexWrap: "wrap" }}>
          <Badge variant="brand">Auto</Badge>
          <Badge variant="info" dot>
            Quoted
          </Badge>
          <Badge variant="warning" dot>
            Renews in 12d
          </Badge>
          <Badge variant="danger" dot>
            Overdue
          </Badge>
          <Badge variant="success" dot>
            Active
          </Badge>
          <Badge variant="neutral">Draft</Badge>
        </div>
      </Card>

      <Card title="Form fields">
        <div style={{ maxWidth: "var(--form-max-width)" }}>
          <Field label="Email" required htmlFor="kit-email" help="We never share this.">
            <Input id="kit-email" type="email" placeholder="agent@example.com" autoComplete="off" />
          </Field>
          <Field label="Password" htmlFor="kit-password">
            <PasswordInput id="kit-password" placeholder="••••••••" autoComplete="new-password" />
          </Field>
          <Field label="Line of business" htmlFor="kit-lob" error="Pick a line of business.">
            <Select id="kit-lob" error defaultValue="">
              <option value="" disabled>
                Choose…
              </option>
              <option>Auto</option>
              <option>Home</option>
              <option>Umbrella</option>
            </Select>
          </Field>
          <Field label="Agent (combobox — bound to records, no free text)">
            <Combobox
              options={[
                { value: "a1", label: "Evan Cleary", description: "Primary agent" },
                { value: "a2", label: "Pat Placeholder", description: "Referring agent" },
                { value: "a3", label: "Sam Specimen", description: "Referring agent" }
              ]}
              value={agent}
              onChange={setAgent}
              placeholder="Search agents…"
            />
          </Field>
        </div>
      </Card>

      <Card title="Overlays & feedback">
        <div style={{ display: "flex", gap: "var(--space-3)", flexWrap: "wrap" }}>
          <Button onClick={() => setModalOpen(true)}>Open modal</Button>
          <Button onClick={() => setDrawerOpen(true)}>Open drawer</Button>
          <Button onClick={() => toast.success("Service request saved")}>Success toast</Button>
          <Button onClick={() => toast.error("Couldn't save the quote — try again")}>
            Error toast
          </Button>
        </div>
      </Card>

      <Card title="Tabs">
        <Tabs
          tabs={[
            { key: "overview", label: "Overview" },
            { key: "policies", label: "Policies", count: 3 },
            { key: "quotes", label: "Quotes", count: 1 },
            { key: "documents", label: "Documents", count: 0 }
          ]}
          active={tab}
          onChange={setTab}
        />
        <p style={{ marginTop: "var(--space-4)", color: "var(--gray-600)" }}>
          Active tab: <strong>{tab}</strong>
        </p>
      </Card>

      <Card title="Loading & empty states">
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "var(--space-5)" }}>
          <div>
            <Skeleton height={20} width="40%" style={{ marginBottom: 12 }} />
            <SkeletonText lines={4} />
          </div>
          <EmptyState
            icon={<SearchIcon size={32} />}
            title="No open service requests"
            message="When a client asks for an ID card or a policy change, it shows up here."
            action={<Button variant="primary">New request</Button>}
          />
        </div>
      </Card>

      <div>
        <h2
          style={{
            font: "600 var(--text-h2)/var(--lh-h2) var(--font-sans)",
            margin: "0 0 var(--space-3)"
          }}
        >
          DataTable (sample data — sort, search, paginate, row click)
        </h2>
        <DataTable
          columns={[
            { key: "household", header: "Household" },
            { key: "policies", header: "Policies", align: "num" },
            {
              key: "premium",
              header: "Total premium",
              align: "num",
              render: (r) => money(r.premium)
            },
            {
              key: "renewal",
              header: "Next renewal",
              render: (r) => dateFmt(r.renewal),
              sortValue: (r) => r.renewal
            },
            {
              key: "status",
              header: "Status",
              render: (r) => (
                <Badge variant={STATUS_VARIANT[r.status] || "neutral"} dot>
                  {r.status}
                </Badge>
              )
            }
          ]}
          rows={SAMPLE_ROWS}
          rowKey={(r) => r.id}
          searchText={(r) => r.household}
          searchPlaceholder="Search households…"
          countLabel="households"
          pageSize={4}
          onRowClick={(r) => toast.info(`Row click → would open ${r.household}'s hub (Phase 2)`)}
        />
      </div>

      <Modal
        open={modalOpen}
        onClose={() => setModalOpen(false)}
        title="Confirm action"
        footer={
          <>
            <Button onClick={() => setModalOpen(false)}>Cancel</Button>
            <Button
              variant="primary"
              onClick={() => {
                setModalOpen(false);
                toast.success("Confirmed");
              }}
            >
              Confirm
            </Button>
          </>
        }
      >
        <p style={{ margin: 0 }}>
          Modals are for focused create/confirm only — one screen, Esc or overlay click closes.
        </p>
      </Modal>

      <Drawer
        open={drawerOpen}
        onClose={() => setDrawerOpen(false)}
        title="Auto · Sample Carrier"
        footer={<Button onClick={() => setDrawerOpen(false)}>Close</Button>}
      >
        <p style={{ marginTop: 0, color: "var(--gray-600)" }}>
          Drawers host reference-heavy content — policy detail, prefilled edit forms. This one shows
          invented placeholder values.
        </p>
        <Field label="Policy number">
          <Input readOnly value="POL-000123 (sample)" />
        </Field>
        <Field label="Annual premium">
          <Input readOnly value="$1,240 (sample)" />
        </Field>
        <Badge variant="warning" dot>
          Renews Sep 10 · 32d
        </Badge>
      </Drawer>
    </div>
  );
}

export default KitPage;
